/**
 * CustomCreativeSession — shell for the shared, human-first Custom Creative
 * Session (CCS). This is the component App.js renders at /ccs.
 *
 * Layout: two columns. LEFT = operator-authored content (stage router:
 * Development → Review → Submitted). RIGHT = the persistent WorkspaceChat,
 * mounted ONCE here at the shell level and keyed only by session id, so
 * switching Development ↔ Review never unmounts the chat.
 *
 * The operator is always the author. Claude is invoked only from the chat.
 * "Copy to field" is bridged from the chat down into the active stage via a
 * ref-registered handler, and flows through the stage's normal autosave.
 *
 * The session id lives in the URL (?session=<id>) so a refresh resumes the
 * same session, stage, fields and chat history.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCcsApi } from './useCcsApi';
import DevelopmentStage from './DevelopmentStage';
import ReviewStage from './ReviewStage';
import WorkspaceChat from './WorkspaceChat';
import OfferSearchSelect from '../OfferSearchSelect';

const DEV_TARGETS = [
  { key: 'hook', label: 'Hook' },
  { key: 'script_body', label: 'Script Body' },
  { key: 'cta', label: 'CTA' },
];
const REVIEW_TARGETS = [
  { key: 'primary_hook', label: 'Primary Hook' },
  { key: 'body', label: 'Body' },
  { key: 'primary_cta', label: 'Primary CTA' },
];

function TwoColumnSkeleton() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="ccs-skeleton">
      <div className="space-y-3">
        <div className="h-24 bg-slate-800/60 rounded-xl animate-pulse" />
        <div className="h-40 bg-slate-800/60 rounded-xl animate-pulse" />
        <div className="h-24 bg-slate-800/60 rounded-xl animate-pulse" />
      </div>
      <div className="h-96 bg-slate-800/60 rounded-xl animate-pulse" />
    </div>
  );
}

function SubmittedView({ session }) {
  const submission = session.submission || {};
  const url = submission.destination_url;
  return (
    <div className="space-y-4" data-testid="ccs-submitted-view">
      <div className="bg-emerald-900/20 border border-emerald-700/40 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-emerald-300">Submitted to production</h3>
        <p className="text-xs text-slate-400 mt-1">This session is now read-only.</p>
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 mt-3 text-sm text-cyan-400 hover:text-cyan-300"
            data-testid="ccs-destination-link"
          >
            Open production card ↗
          </a>
        ) : (
          <p className="text-xs text-slate-500 mt-3">Routed. Card link not available yet.</p>
        )}
      </div>

      {/* Read-only content recap */}
      <ReadonlyRecap session={session} />
    </div>
  );
}

function ReadonlyRecap({ session }) {
  const a = session.artifacts || {};
  const hooks = a.hooks || [];
  const ctas = a.ctas || [];
  const body = a.body || null;
  const brief = session.brief || {};
  const Row = ({ label, children }) => (
    <div>
      <span className="text-xs text-slate-500 block mb-1">{label}</span>
      <div className="text-sm text-slate-200 whitespace-pre-wrap">{children}</div>
    </div>
  );
  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 space-y-3" data-testid="ccs-readonly-recap">
      <Row label="Hooks">
        {hooks.length ? hooks.map((h) => <p key={h.artifact_id}>• {h.text || '—'}</p>) : '—'}
      </Row>
      <Row label="Script Body">{body?.text || '—'}</Row>
      <Row label="CTAs">
        {ctas.length ? ctas.map((c) => <p key={c.artifact_id}>• {c.text || '—'}</p>) : '—'}
      </Row>
      <Row label="Creative Brief">{brief.brief_text || '—'}</Row>
      <Row label="Production Instructions">{brief.production_instructions || '—'}</Row>
      <Row label="Video Format">{session.draft?.video_format || '—'}</Row>
    </div>
  );
}

export default function CustomCreativeSession() {
  const api = useCcsApi();
  const [params, setParams] = useSearchParams();
  const sessionId = params.get('session') || '';

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // start card
  const [offerId, setOfferId] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  // copy-to-field bridge: the active stage registers a handler here.
  const copyHandlerRef = useRef(null);
  const setCopyHandler = useCallback((fn) => { copyHandlerRef.current = fn; }, []);
  const handleCopyToField = useCallback((key, text) => {
    if (copyHandlerRef.current) copyHandlerRef.current(key, text);
  }, []);

  const loadSession = useCallback(async (targetId) => {
    setLoading(true);
    setLoadError(null);
    try {
      setSession(await api.getSession(targetId));
    } catch (e) {
      setLoadError('Could not load this session.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (sessionId) loadSession(sessionId);
    else setSession(null);
  }, [sessionId, loadSession]);

  const refreshSession = useCallback(async () => {
    if (sessionId) await loadSession(sessionId);
  }, [sessionId, loadSession]);

  const applySession = useCallback((s) => { if (s) setSession(s); }, []);

  const handleStart = async () => {
    if (!offerId || creating) return;
    setCreating(true);
    setCreateError(null);
    try {
      const s = await api.createSession(offerId);
      setSession(s);
      setParams({ session: s.session_id });
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      let msg;
      if (status === 404 && (!detail || detail === 'Not Found')) {
        // The endpoint's flag guard returns a bare 404 "Not Found" when the
        // backend CCS flag is off — surface that rather than the raw string.
        msg = 'Creative service unavailable (404). The Custom Creative Session is not enabled on the backend (CCS_ENABLED) or is unreachable — ask an engineer to confirm the backend flag.';
      } else if (status === 404 && detail === 'Offer not found') {
        msg = `Offer not found: ${offerId}. Choose a different offer.`;
      } else if (status === 401 || status === 403) {
        msg = 'Not authorized to start a creative session. Please re-authenticate.';
      } else if (detail) {
        msg = `Could not start the session: ${detail}`;
      } else {
        msg = 'Could not start the session — check your connection and try again.';
      }
      setCreateError(msg);
    } finally {
      setCreating(false);
    }
  };

  // ---- Render: session creation (no session yet) ----
  if (!sessionId && !session) {
    return (
      <div className="max-w-lg mx-auto mt-8" data-testid="ccs-start-card">
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Custom Creative Session</h2>
            <p className="text-slate-400 text-sm mt-1">Pick an offer to anchor the session, then start.</p>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-400 block mb-1">Offer</label>
            <OfferSearchSelect
              value={offerId}
              onChange={(oid) => setOfferId(oid)}
              placeholder="Search offers…"
              testIdPrefix="ccs-offer-select"
            />
          </div>
          {createError && <p className="text-xs text-rose-400" data-testid="ccs-create-error">{createError}</p>}
          <button
            type="button"
            onClick={handleStart}
            disabled={!offerId || creating}
            className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 transition-colors text-sm font-medium disabled:opacity-40"
            data-testid="ccs-start-btn"
          >
            {creating ? 'Starting…' : 'Start Session'}
          </button>
        </div>
      </div>
    );
  }

  // ---- Render: loading an existing session ----
  if (loading && !session) {
    return (
      <div className="p-2" data-testid="ccs-loading">
        <TwoColumnSkeleton />
      </div>
    );
  }

  // ---- Render: load failure ----
  if (loadError && !session) {
    return (
      <div className="max-w-lg mx-auto mt-8 text-center" data-testid="ccs-load-error">
        <p className="text-sm text-rose-400">{loadError}</p>
        <button
          type="button"
          onClick={() => loadSession(sessionId)}
          className="mt-3 px-4 py-2 bg-slate-700 text-slate-200 rounded-lg hover:bg-slate-600 text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="p-2" data-testid="ccs-loading">
        <TwoColumnSkeleton />
      </div>
    );
  }

  const stage = session.stage;
  const copyTargets = stage === 'REVIEW' ? REVIEW_TARGETS : DEV_TARGETS;

  let leftColumn;
  if (stage === 'DEVELOPMENT') {
    leftColumn = (
      <DevelopmentStage
        session={session}
        api={api}
        applySession={applySession}
        refreshSession={refreshSession}
        setCopyHandler={setCopyHandler}
      />
    );
  } else if (stage === 'REVIEW') {
    leftColumn = (
      <ReviewStage
        session={session}
        api={api}
        applySession={applySession}
        refreshSession={refreshSession}
        setCopyHandler={setCopyHandler}
      />
    );
  } else {
    leftColumn = <SubmittedView session={session} />;
  }

  const STAGE_LABEL = { DEVELOPMENT: 'Development', REVIEW: 'Review', SUBMITTED: 'Submitted' };

  return (
    <div className="space-y-4" data-testid="ccs-shell">
      {/* Header / stage indicator */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">{session.offer_name || 'Custom Creative Session'}</h2>
          <div className="flex items-center gap-2 mt-1">
            {['DEVELOPMENT', 'REVIEW', 'SUBMITTED'].map((st, i) => (
              <React.Fragment key={st}>
                {i > 0 && <span className="text-slate-600 text-xs">→</span>}
                <span
                  className={`text-xs px-2 py-0.5 rounded ${
                    stage === st ? 'bg-emerald-500/15 text-emerald-400' : 'text-slate-500'
                  }`}
                >
                  {STAGE_LABEL[st]}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Two-column layout. Chat is mounted ONCE here (keyed by session id
          only) so it survives Development ↔ Review stage switches. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <div className="min-w-0">{leftColumn}</div>
        <div className="lg:sticky lg:top-4 h-[calc(100vh-8rem)] min-h-[400px]">
          <WorkspaceChat
            key={session.session_id}
            sessionId={session.session_id}
            api={api}
            copyTargets={copyTargets}
            onCopyToField={handleCopyToField}
            readOnly={stage === 'SUBMITTED'}
          />
        </div>
      </div>
    </div>
  );
}
